/**
 * The pointer, the keyboard and the tool in hand — `property-maps-v2.md` §9.3.
 *
 * The tools are pure (`draw/`), so something has to turn a MapLibre event
 * into a `DrawEvent` and decide when a gesture is over. That is all this is,
 * and keeping it here rather than inside the tools is what lets the tools be
 * tested with synthetic events and no browser (§13).
 *
 * **The map is the projection.** `unproject`/`project` are the `MapFrame`, so
 * a point placed at a screen pixel is the world point under it at whatever
 * zoom the person happens to be at — which is also why snapping is measured
 * in pixels (`draw/snap.ts`).
 *
 * **A finished shape leaves immediately.** §12 rule 9: shapes autosave, so
 * `onFinished` is the save, and the tool resets itself for the next one.
 * Nothing waits for a Done button that does not exist.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import { geometryProblem, type Geometry, type LngLat } from '@rowted/shared'
import { snapper, toolFor, type Draft, type MapFrame, type ToolName } from '../draw/index.js'
import { crosshairFor, type Crosshair } from '../draw/crosshair.js'

export interface DrawingResult {
  /** What is being drawn right now, for the canvas to paint dashed. */
  draft: Geometry | null
  /** The chip that follows the tool, or null when nothing is in hand. */
  hint: string | null
  /**
   * The crosshair's four buttons, in crosshair mode (§6.2).
   *
   * Null on a desktop, where a pointer places points directly. The tool is
   * the **same** tool either way — this is an adapter over it, not a second
   * state machine — so a rectangle drawn with a thumb and one drawn with a
   * mouse are the same rectangle.
   */
  bar: {
    points: number
    complete: boolean
    addPoint: () => void
    undoPoint: () => void
    finish: () => void
    cancel: () => void
  } | null
}

export function useDrawing({
  map, tool, targets, pin, crosshair, onFinished, onProblem,
}: {
  map: MapLibreMap | null
  tool: ToolName
  /** The geometries snapping may land on — visible layers only (§5.3). */
  targets: () => { geometry: Geometry }[]
  /** The property's pin, for the 5 km guard. */
  pin: LngLat
  /**
   * Place points under a fixed crosshair instead of under the pointer.
   *
   * §6.2: fingers cover what they trace, so on a phone the map moves and the
   * sight stays still. The map's pointer listeners are not attached at all
   * in this mode — a tap would otherwise place a point *and* the bar would
   * place another.
   */
  crosshair?: boolean
  onFinished: (draft: Draft) => void
  /** A shape that cannot be saved stays in edit and this says why (§9.3). */
  onProblem: (message: string) => void
}): DrawingResult {
  const [draft, setDraft] = useState<Geometry | null>(null)
  /* The callbacks change on every render of the editor; the listeners must
     not be torn down and rebuilt each time, so they are read through a ref. */
  const handlers = useRef({ onFinished, onProblem, targets })
  handlers.current = { onFinished, onProblem, targets }

  const frame = useMemo<MapFrame | null>(() => (map ? {
    toLngLat: ({ x, y }) => {
      const at = map.unproject([x, y])
      return [at.lng, at.lat]
    },
    toPoint: ([lng, lat]) => {
      const at = map.project([lng, lat])
      return { x: at.x, y: at.y }
    },
  } : null), [map])

  const live = useMemo(() => (frame
    ? toolFor(tool, { frame, snap: snapper(frame, () => handlers.current.targets()) })
    : null), [frame, tool])

  /* The crosshair over the same tool, rebuilt whenever the tool changes. */
  const sight = useMemo<Crosshair | null>(
    () => (live && crosshair ? crosshairFor(live) : null), [live, crosshair])
  const [points, setPoints] = useState(0)

  useEffect(() => { setPoints(0) }, [sight])

  useEffect(() => {
    if (!map || !live || crosshair) {
      setDraft(null)
      return undefined
    }

    const paint = () => setDraft(live.draft()?.geometry ?? null)

    const done = () => {
      const made = live.finish()
      if (!made) return
      const problem = geometryProblem(made.kind, made.geometry, pin)
      if (problem) {
        /* §9.3: the shape stays in edit and the toast says what to adjust.
           `finish()` already cleared the tool, so it is put back. */
        handlers.current.onProblem(problem)
      } else {
        handlers.current.onFinished(made)
      }
      setDraft(null)
    }

    const shift = (event: { originalEvent: { shiftKey?: boolean } }) =>
      Boolean(event.originalEvent.shiftKey)

    type Pointer = { point: { x: number; y: number }; originalEvent: MouseEvent }

    const down = (event: Pointer) => {
      live.onPointerDown({ point: event.point, shift: shift(event) })
      paint()
    }
    const move = (event: Pointer) => {
      live.onPointerMove({ point: event.point, shift: shift(event) })
      paint()
    }
    const up = (event: Pointer) => {
      live.onPointerUp({ point: event.point, shift: shift(event) })
      if (live.autoFinish && live.draft()?.complete) done()
      else paint()
    }
    const twice = (event: Pointer) => {
      /* Or the map zooms in under the shape somebody just finished. */
      event.originalEvent.preventDefault()
      done()
    }

    const key = (event: KeyboardEvent) => {
      /* Never while somebody is typing into the shape popup's name field. */
      const target = event.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return
      if (event.key === 'Enter') { done(); return }
      live.onKey({ key: event.key, shift: event.shiftKey })
      paint()
    }

    /*
     * **A finger is not a mouse, and MapLibre does not pretend it is.**
     *
     * These handlers used to be the mouse three alone, which is correct on a
     * desktop and draws nothing at all anywhere else: MapLibre emits
     * `touchstart`/`touchmove`/`touchend` for a touch and does not synthesise
     * `mousedown` from them. So §6.2.3's default gesture — drag a Rect or a
     * Circle with one finger — did nothing on a phone, and on an iPad, where
     * §7 leaves the crosshair *off* by default, none of the four drawing
     * tools worked with a finger or a pencil. Nothing failed: a press, a
     * drag and a release simply panned the map.
     *
     * **One finger only.** Two is a pinch, which stays a zoom — passing a
     * second contact to a tool would put a rectangle's corner wherever the
     * second thumb happened to land.
     *
     * `touchend` carries `changedTouches`, not `touches`, so MapLibre's
     * `point` can be absent on it; the last point seen during the drag is
     * what the tool gets instead, which is where the finger was.
     */
    let last: { x: number; y: number } | null = null

    type Touched = {
      point?: { x: number; y: number }
      points?: { x: number; y: number }[]
      originalEvent: TouchEvent
    }

    /** One contact, or nothing — a pinch is the map's gesture, not a tool's. */
    const oneFinger = (event: Touched): { x: number; y: number } | null => {
      const touches = event.originalEvent.touches.length
      if (touches > 1) return null
      const at = event.point ?? event.points?.[0] ?? last
      return at ?? null
    }

    const touchDown = (event: Touched) => {
      const at = oneFinger(event)
      if (!at) return
      last = at
      live.onPointerDown({ point: at, shift: false })
      paint()
    }
    const touchMove = (event: Touched) => {
      const at = oneFinger(event)
      if (!at) return
      last = at
      live.onPointerMove({ point: at, shift: false })
      paint()
    }
    const touchUp = (event: Touched) => {
      /* `touches` is empty on the last finger up, so `oneFinger`'s guard is
         about the fingers still down — a pinch releasing one at a time must
         not finish a shape. */
      if (event.originalEvent.touches.length > 0) return
      const at = event.point ?? last
      if (!at) return
      live.onPointerUp({ point: at, shift: false })
      if (live.autoFinish && live.draft()?.complete) done()
      else paint()
      last = null
    }

    map.on('mousedown', down)
    map.on('mousemove', move)
    map.on('mouseup', up)
    map.on('dblclick', twice)
    map.on('touchstart', touchDown)
    map.on('touchmove', touchMove)
    map.on('touchend', touchUp)
    window.addEventListener('keydown', key)

    return () => {
      map.off('mousedown', down)
      map.off('mousemove', move)
      map.off('mouseup', up)
      map.off('dblclick', twice)
      map.off('touchstart', touchDown)
      map.off('touchmove', touchMove)
      map.off('touchend', touchUp)
      window.removeEventListener('keydown', key)
      live.cancel()
      setDraft(null)
    }
  }, [map, live, crosshair, pin])

  /**
   * The same `done()` the pointer path uses, for the bar's ✓.
   *
   * It is written twice rather than shared because the two differ in exactly
   * one way — the pointer path runs inside an effect that owns the map's
   * listeners — and a shared closure would have to be held in a ref and read
   * from both, which is more machinery than the six lines it saves.
   */
  const bar = useMemo(() => (sight ? {
    get points() { return points },
    get complete() { return sight.complete },
    addPoint: () => {
      if (!map) return
      const box = map.getCanvas()
      sight.addPoint({ x: box.clientWidth / 2, y: box.clientHeight / 2 })
      setDraft(sight.draft()?.geometry ?? null)
      setPoints(sight.points)
    },
    undoPoint: () => {
      sight.undoPoint()
      setDraft(sight.draft()?.geometry ?? null)
      setPoints(sight.points)
    },
    finish: () => {
      const made = sight.finish()
      if (!made) return
      const problem = geometryProblem(made.kind, made.geometry, pin)
      if (problem) handlers.current.onProblem(problem)
      else handlers.current.onFinished(made)
      setDraft(null)
      setPoints(0)
    },
    cancel: () => { sight.cancel(); setDraft(null); setPoints(0) },
  } : null), [sight, map, pin, points])

  return { draft, hint: live?.hint ?? null, bar }
}
